import { useHtmlPresignRewrite } from "@/hooks/useHtmlPresignRewrite";
import { useCanvasUi } from "@/pages/canvas/context/CanvasUiContext";
import { useHtmlBlockEditSession } from "@/pages/canvas/hooks/useHtmlBlockEditSession";
import {
  useHtmlBlockFocus,
  type HtmlBlockFocusMode,
} from "@/pages/canvas/hooks/useHtmlBlockFocus";
import {
  useThemeContext,
  type BlockDisplay,
  type BlockEntryPoint,
  type BlockToParentMessage,
  type HtmlIslandAutosaveState,
} from "@cohesive/ui-components";
import { useStoreApi } from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface UseHtmlBlockNodeParams {
  nodeId: string;
  /** The block's resolved display model (blockDisplayFromNodeData). */
  display: BlockDisplay;
  /** File record id backing this block, when hydrated. */
  fileRecordId?: string;
  /** Node still showing its loading skeleton. */
  loading: boolean;
  selected: boolean;
  dragging: boolean;
  canEdit: boolean;
}

export interface HtmlBlockNodeState {
  display: BlockDisplay;
  /** App theme, threaded into the card so the iframe's injected color scheme
   *  matches its embedder (a mismatch paints an opaque backstop and silently
   *  breaks transparent blocks). */
  theme: "light" | "dark";
  /** Swaps embedded public file URLs for presigned ones before render. */
  transformHtml: (html: string) => Promise<string>;
  /** Which mode the user is in on this block, if any. */
  focusMode: HtmlBlockFocusMode | null;
  /**
   * Enter / toolbar routes INTO this block. True when entering buys
   * something: interaction (any loaded block), or island editing.
   */
  canEnter: boolean;
  /**
   * Enter the block (Enter key, toolbar, an edit-request from the frame):
   * edit mode when the user can edit and the document has islands (a
   * superset of interaction — buttons outside islands still work), otherwise
   * interact mode. The optional point is the gesture position in iframe
   * content coordinates; an edit entry forwards it so the caret lands under
   * the pointer.
   */
  enter: (point?: BlockEntryPoint) => void;
  /**
   * Toolbar entry: enter exactly as Enter would, tolerant of the capability
   * arriving one render late. Arms a one-shot that fires as soon as entry is
   * possible, and disarms on deselection so a never-capable click can't
   * enter later.
   */
  requestEnter: () => void;
  /** Foot chrome copy announcing what the gestures do on this block. */
  enterHintLabel: string;
  /** The live document reports at least one editable island. */
  hasIslands: boolean;
  /** Hovering the card should show the island glow (pre-entry affordance). */
  islandHint: boolean;
  /** Caret target for the current edit session's entry gesture, if any. */
  editFocusPoint: BlockEntryPoint | null;
  /** Runtime ready handshake — carries the live island count. */
  onRuntimeReady: (info: { islands: number }) => void;
  /**
   * The iframe should receive pointer events. Being inside the block is
   * necessary but not sufficient: an in-flight drag or the hand tool means
   * the canvas is mid-gesture and must keep the pointer.
   */
  iframeOwnsPointer: boolean;
  /** Runtime messages from the block's injected script. */
  onBlockMessage: (msg: BlockToParentMessage) => void;
  /** Enter inline interactive mode directly — the click-through doorway:
   *  a forwarded click that lands on a control activates it and hands the
   *  block the real pointer, so everything after is native. */
  enterInteractive: () => void;
  /** Leave whatever mode this block is in. */
  exit: () => void;
  /** An edit session is active — islands are contenteditable. */
  islandsEditable: boolean;
  /** Start/stop the edit session (toolbar pencil). */
  toggleEditSession: () => void;
  /** Hold refetches out of the iframe while editing/saving/settling. */
  contentFrozen: boolean;
  /** Pending/saving counts for the grip pill. */
  saveState: HtmlIslandAutosaveState;
}

/**
 * Everything an HTML block node needs, gathered in one place.
 *
 * Mounted ONLY by the block wrapper in EntityNode — i.e. only for nodes whose
 * display model resolves to a block. That is why this may freely use hooks
 * that cost something (a presign map, latch subscriptions, and later a
 * react-query mutation): an image or PDF node never mounts them.
 *
 * Gesture model: at rest the block is shielded and a single click is
 * forwarded into the frame — a control activates and the block goes live
 * inline (the doorway), editable text opens an edit session with the caret
 * at the point, plain content does nothing. Enter and the toolbar route to
 * the most useful mode (edit when canEdit + islands, else interact).
 * Double-click is the "open" gesture, owned by the caller. The foot chrome
 * announces the gestures, so routing never hinges on invisible state.
 */
export function useHtmlBlockNode({
  nodeId,
  display,
  fileRecordId,
  loading,
  selected,
  dragging,
  canEdit,
}: UseHtmlBlockNodeParams): HtmlBlockNodeState {
  const { mode: themeMode } = useThemeContext();
  const theme = themeMode === "dark" ? "dark" : "light";
  const { cursorTool } = useCanvasUi();
  const storeApi = useStoreApi();

  const presign = useHtmlPresignRewrite({ fileRecordId });

  // Interaction is empirical, not declared: any loaded block can go live
  // (the click-through doorway decides per click whether something took
  // it). Deliberately not gated on canEdit — viewers of a published canvas
  // can use an interactive block.
  const canInteract = !loading;

  // The live document's island count, reported by the runtime's ready
  // handshake (the frame is opaque-origin, so this is the host's only view).
  const [hasIslands, setHasIslands] = useState(false);
  const onRuntimeReady = useCallback((info: { islands: number }) => {
    setHasIslands(info.islands > 0);
  }, []);

  const canEnterEdit = canEdit && !loading;
  // Editing is only a useful entry target when there is something to edit.
  const canEnterEditIslands = canEnterEdit && hasIslands;

  // `flush` is read through a ref because the session hook needs the focus
  // latch (for `editing`) and the latch needs the session (to flush on its
  // falling edge) — the ref breaks that cycle without deferring the flush.
  const flushRef = useRef<() => void>(() => {});
  const onModeEnd = useCallback((previousMode: HtmlBlockFocusMode) => {
    if (previousMode === "edit") flushRef.current();
  }, []);

  const focus = useHtmlBlockFocus({
    nodeId,
    canInteract,
    canEnterEdit,
    selected,
    dragging,
    onModeEnd,
  });

  const editing = focus.mode === "edit";
  const session = useHtmlBlockEditSession({
    fileRecordId,
    editing,
    reversePresignedUrls: presign.reversePresignedUrls,
  });
  flushRef.current = session.flush;

  // Caret target for the session being entered by gesture; cleared when the
  // session ends so a toolbar-pencil session never inherits a stale point.
  const [editFocusPoint, setEditFocusPoint] = useState<BlockEntryPoint | null>(
    null,
  );
  useEffect(() => {
    if (!editing) setEditFocusPoint(null);
  }, [editing]);

  const canEnter = (canEnterEditIslands || canInteract) && focus.mode === null;

  const enter = useCallback(
    (point?: BlockEntryPoint) => {
      // Edit is the superset mode (pointer live + islands editable), so it
      // wins when available; interact covers viewers and island-less blocks.
      if (canEnterEditIslands) {
        setEditFocusPoint(point ?? null);
        focus.enterEdit();
        return;
      }
      if (canInteract) focus.enterInteract();
    },
    [canEnterEditIslands, canInteract, focus],
  );

  // One-shot deferred entry for the toolbar (see the interface comment).
  const [enterRequested, setEnterRequested] = useState(false);
  const requestEnter = useCallback(() => setEnterRequested(true), []);
  useEffect(() => {
    if (!enterRequested) return;
    // Deselection wins over capability — the two can land in the SAME render@
    // (the persisted flag arrives after a click-away), and entering would
    // steal selection back via enter()'s addSelectedNodes.
    if (!selected) {
      setEnterRequested(false);
      return;
    }
    if (canEnterEditIslands || canInteract) {
      setEnterRequested(false);
      enter();
    }
  }, [enterRequested, canEnterEditIslands, canInteract, enter, selected]);

  // Keyboard drill-in (Figma's Enter): enter the selected block. Window-level
  // because React Flow nodes don't reliably hold DOM focus; guarded so typing
  // contexts and multi-selection never trigger it.
  const canEnterRef = useRef(canEnter);
  canEnterRef.current = canEnter;
  const enterRef = useRef(enter);
  enterRef.current = enter;
  useEffect(() => {
    if (!selected || focus.mode !== null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }
      if (!canEnterRef.current) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      // Only when this block is the single selection — Enter over a marquee
      // of nodes must not race every selected block for the latch.
      const nodes = storeApi.getState().nodes;
      let selectedCount = 0;
      for (const node of nodes) {
        if (node.selected && ++selectedCount > 1) break;
      }
      if (selectedCount !== 1) return;
      event.preventDefault();
      enterRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selected, focus.mode, storeApi]);

  // The click-through doorway: a forwarded click that hit a control hands
  // the block the real pointer. From REST only — an island click issues an
  // edit-request AND a click-result for the same gesture (the result also
  // feeds the double-click guard), and the result arrives second; entering
  // interact here would downgrade the edit session the request just opened,
  // leaving islands without their editing state.
  const enterInteractive = useCallback(() => {
    if (focus.mode === null) focus.enterInteract();
  }, [focus]);

  const toggleEditSession = useCallback(() => {
    if (editing) focus.exit();
    else focus.enterEdit();
  }, [editing, focus]);

  const onBlockMessage = useCallback(
    (msg: BlockToParentMessage) => {
      // Escape inside the iframe: the parent's key listener can't see it,
      // because the activated block holds focus in another document.
      if (msg.type === "exit-request") {
        focus.exit();
        return;
      }
      if (msg.type === "edit-request") {
        // Editable text was clicked (at rest, or natively in interact mode):
        // open the edit session with the caret at the point. enter() already
        // routes to interact when editing isn't possible.
        enterRef.current({ x: msg.x, y: msg.y });
        return;
      }
      session.onIslandMessage(msg);
    },
    [focus, session],
  );

  const iframeOwnsPointer =
    focus.mode !== null && !dragging && cursorTool !== "hand";

  // Announce what the gestures do BEFORE they happen — this label is what
  // turns the per-block routing from a trap into a labeled affordance. Single
  // click operates the block (controls activate, editable text takes the
  // caret); double-click opens it. Loading blocks are inert.
  const enterHintLabel = canInteract
    ? "Click to use · double-click to open"
    : "Double-click to open";

  // Hover-at-rest island highlighting is retired (D6); the edit session
  // keeps its own indication.
  const islandHint = false;

  return useMemo(
    () => ({
      display,
      theme,
      transformHtml: presign.transformHtml,
      focusMode: focus.mode,
      canEnter,
      enter,
      requestEnter,
      enterHintLabel,
      hasIslands,
      islandHint,
      editFocusPoint,
      onRuntimeReady,
      iframeOwnsPointer,
      onBlockMessage,
      enterInteractive,
      exit: focus.exit,
      islandsEditable: editing,
      toggleEditSession,
      contentFrozen: session.contentFrozen,
      saveState: session.saveState,
    }),
    [
      display,
      theme,
      presign.transformHtml,
      focus.mode,
      focus.exit,
      canEnter,
      enter,
      requestEnter,
      enterHintLabel,
      hasIslands,
      islandHint,
      editFocusPoint,
      onRuntimeReady,
      iframeOwnsPointer,
      onBlockMessage,
      enterInteractive,
      editing,
      toggleEditSession,
      session.contentFrozen,
      session.saveState,
    ],
  );
}
